import { useEffect, useState } from 'react';

// Day / month / year selects. The picker keeps its own local state
// for the three parts so a partial selection (e.g. just the day) is
// preserved on screen — the previous implementation derived parts
// from the parent's ISO value and reset to placeholders the moment
// any single field was missing, which made it impossible to fill
// the inputs one at a time.
//
// onChange fires with the ISO YYYY-MM-DD string only when all three
// parts are present AND form a valid calendar date (e.g. Feb 30
// stays empty). Otherwise we emit '' so the parent can keep its
// "invalid until complete" gate.

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseValue(value) {
  if (!value) return { y: '', m: '', d: '' };
  const [y, m, d] = value.split('-');
  return {
    y: y || '',
    m: m ? String(parseInt(m, 10)) : '',
    d: d ? String(parseInt(d, 10)) : ''
  };
}

function composeIso({ y, m, d }) {
  if (!y || !m || !d) return '';
  const candidate = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  if (
    candidate.getUTCFullYear() === Number(y) &&
    candidate.getUTCMonth() === Number(m) - 1 &&
    candidate.getUTCDate() === Number(d)
  ) {
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return '';
}

export default function DobPicker({ value, onChange }) {
  const [parts, setParts] = useState(() => parseValue(value));

  // If the parent updates `value` from outside (e.g. resetting the
  // form), seed our local state from it. We only sync the parent →
  // local direction when the parent's ISO actually corresponds to a
  // *complete* date, so an empty value from "partial → invalid"
  // doesn't wipe the user's typing.
  useEffect(() => {
    if (!value) return;
    setParts(parseValue(value));
  }, [value]);

  const thisYear = new Date().getUTCFullYear();
  const YEARS = Array.from({ length: 100 }, (_, i) => thisYear - 13 - i);

  const setPart = (key) => (e) => {
    const next = { ...parts, [key]: e.target.value };
    setParts(next);
    onChange?.(composeIso(next));
  };

  return (
    <div className="row" style={{ gap: 8 }}>
      <select value={parts.d} onChange={setPart('d')} aria-label="Day">
        <option value="">Day</option>
        {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <select value={parts.m} onChange={setPart('m')} aria-label="Month">
        <option value="">Month</option>
        {MONTHS.map((label, i) => <option key={label} value={i + 1}>{label}</option>)}
      </select>
      <select value={parts.y} onChange={setPart('y')} aria-label="Year">
        <option value="">Year</option>
        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}
